import { ReactElement } from "react";
import { proxy, ref, useSnapshot } from "valtio";

import { MenuId, MenuIds } from "./useMenus";
import { BottomMenuId, BottomMenuPath, BottomMenuPaths } from "../components/bottom-menu/paths";

interface IBottomMenuContextItem {
	/** Id is used to know the tree or replace a specific item  */
	id: string;

	/** The react node to show for this menu id */
	menuItem: ReactElement;
}

/** Load a new stack of bottom menu's to get to a specified path */
function loadBottomMenuPathAsync(
	currentStack: ReadonlyArray<IBottomMenuContextItem>,
	path: string,
	getMenuAsync: (menuId: string) => Promise<ReactElement | undefined>,
) {
	const pathParts = (path || "").split("/");

	// Find the common root index
	let commonRootIndex = -1;
	currentStack.every((menuItem, index) => {
		if (pathParts[index] !== menuItem.id) return false; // other path
		commonRootIndex = index;
		return true;
	});

	// Are we already on the right position?
	if (pathParts.length - 1 === commonRootIndex && currentStack.length - 1 === commonRootIndex) {
		return Promise.resolve(currentStack);
	}

	// Target is already available as parent?
	if (pathParts.length - 1 === commonRootIndex) {
		return Promise.resolve(currentStack.slice(0, commonRootIndex + 1));
	}

	// Re-use common root menus
	const newState = currentStack.slice(0, commonRootIndex + 1);

	// Can we load childs
	return pathParts
		.slice(commonRootIndex + 1) // remove parts from path already loaded
		.reduce(
			(promise, pathPart, newPathIndex) =>
				promise.then((stack) => {
					// Check if we need to stop
					if (newPathIndex >= stack.length - commonRootIndex) return stack;

					// Get Menu Component
					return getMenuAsync(pathPart).then((menuItem) => {
						if (menuItem) {
							stack.push({
								id: pathPart,
								menuItem,
							});
						}
						return stack;
					});
					// get next item
				}),
			Promise.resolve(newState),
		);
}

interface IBottomMenuState {
	/** Stack of menu items, only use from BottomMenu*/
	stack: ReadonlyArray<IBottomMenuContextItem>;

	/** Last requested menu path that is loading (async) */
	loadingPath: string | undefined;

	/** The current path (The menuId's separated with '/') */
	readonly currentPath: string | undefined;

	/** Returns true if the menu with this Id is visible in the BottomMenu */
	isVisible(buttonMenuId: BottomMenuId): boolean;

	// Set the function that creates menu's
	setComponentCreator(
		createMenuAsync: (menuId: string) => Promise<ReactElement | undefined>,
	): void;

	/** A function used to create menu items, needs to be set */
	createMenuAsync: ((menuId: string) => Promise<ReactElement | undefined>) | undefined;

	/** Navigate to a specific item, create items when needed */
	gotoPathAsync(pathData: string | undefined): Promise<void> | undefined;
}

// convert the menuId to a list of components to render
export function getMenuIdToBottomMenuPath(menuId: MenuId): BottomMenuPath {
	switch (menuId) {
		case MenuIds.AlternativeProducts:
			return BottomMenuPaths.AlternativeProducts;
		case MenuIds.BackgroundColor:
			return BottomMenuPaths.BackgroundColor;
		case MenuIds.BackgroundPattern:
			return BottomMenuPaths.BackgroundPattern;
		case MenuIds.Backgrounds:
			return BottomMenuPaths.Backgrounds;
		case MenuIds.Designs:
			return BottomMenuPaths.Designs;
		case MenuIds.Events:
			return BottomMenuPaths.Root;
		case MenuIds.FontFamilies:
			return BottomMenuPaths.FontFamilies;
		case MenuIds.Layouts:
			return BottomMenuPaths.Layouts;
		case MenuIds.Map:
			return BottomMenuPaths.Root;
		case MenuIds.None:
			return BottomMenuPaths.Root; // OK?, rename root?
		case MenuIds.Options:
			return BottomMenuPaths.Options;
		case MenuIds.Photo:
			return BottomMenuPaths.Photo;
		case MenuIds.PhotoBrightness:
			return BottomMenuPaths.PhotoBrightness;
		case MenuIds.PhotoContrast:
			return BottomMenuPaths.PhotoContrast;
		case MenuIds.PhotoEditing:
			return BottomMenuPaths.PhotoEditing;
		case MenuIds.PhotoFilters:
			return BottomMenuPaths.PhotoFilters;
		case MenuIds.PhotoRotate:
			return BottomMenuPaths.PhotoTransform;
		case MenuIds.PhotoSaturation:
			return BottomMenuPaths.PhotoSaturation;
		case MenuIds.PhotoSelection:
			return BottomMenuPaths.PhotoSelection;
		case MenuIds.PhotoTransform:
			return BottomMenuPaths.PhotoTransform;
		case MenuIds.ProductOptions:
			return BottomMenuPaths.ProductOptions;
		case MenuIds.PhotoZoneDepth:
			return BottomMenuPaths.PhotoZoneDepth;
		case MenuIds.Qr:
			return BottomMenuPaths.Qr;
		case MenuIds.QrAdd:
			return BottomMenuPaths.QrAdd;
		case MenuIds.QrEdit:
			return BottomMenuPaths.QrEdit;
		case MenuIds.Root:
			return BottomMenuPaths.Root;
		case MenuIds.Symbols:
			return BottomMenuPaths.Symbols;
		case MenuIds.SymbolsList:
			return BottomMenuPaths.SymbolsList;
		case MenuIds.SymbolZoneDepth:
			return BottomMenuPaths.SymbolsZoneDepth;
		case MenuIds.Text:
			return BottomMenuPaths.Text;
		case MenuIds.TextHAlignment:
		case MenuIds.TextVAlignment:
			return BottomMenuPaths.TextAlignment;
		case MenuIds.TextColor:
			return BottomMenuPaths.TextColor;
		case MenuIds.TextSize:
			return BottomMenuPaths.TextSize;
		case MenuIds.TextStyle:
			return BottomMenuPaths.TextStyle;
		case MenuIds.TextValue:
			return BottomMenuPaths.TextValue;
		case MenuIds.TextZoneAdd:
			return BottomMenuPaths.Text;
		case MenuIds.TextZoneDepth:
			return BottomMenuPaths.TextZoneDepth;
		case MenuIds.DateSelection:
			return BottomMenuPaths.DateSelection;
		default:
			eXpress.utils.exhaustiveFail(menuId);
			return BottomMenuPaths.Root;
	}
}

/** Create a function, so unit tests can create a fresh instance */
export const createBottomMenuState = () => {
	const bottomMenuState = proxy<IBottomMenuState>({
		stack: [],
		loadingPath: undefined,
		createMenuAsync: undefined,

		/** Computed value from the stack */
		get currentPath(): string | undefined {
			return bottomMenuState.stack.length
				? bottomMenuState.stack.map((i) => i.id).join("/")
				: undefined;
		},

		isVisible(buttonMenuId): boolean {
			return bottomMenuState.stack.some((item) => item.id === buttonMenuId);
		},

		setComponentCreator(createMenuAsync) {
			bottomMenuState.createMenuAsync = ref(createMenuAsync); // ref no tacking is needed
		},

		gotoPathAsync(path): Promise<void> | undefined {
			const state = bottomMenuState;

			// Shortcut: Remove all
			if (path === undefined) {
				state.stack = [];
				state.loadingPath = undefined; // New stack set, nothing loading
				return undefined;
			}

			if (!state.createMenuAsync) {
				// Bottom menu not active (desktop layout?)
				// Should we keep current stack or default to root when comming back?
				return undefined;
			}

			// Indicate that it is loading
			state.loadingPath = path;

			return loadBottomMenuPathAsync(
				state.stack, // Previous path
				path,
				state.createMenuAsync,
			)
				.then((newStack) => {
					if (!newStack) return;

					// Still update if menu is disabled after update request?
					// If already another menu is started to load during async load, cancel update
					if (state.loadingPath !== path) return;

					state.stack = newStack.map((item) => ({
						id: item.id,
						menuItem: ref(item.menuItem || undefined), // Don't proxy a ReactNode
					}));
					state.loadingPath = undefined; // Disable loading
				})
				.catch((err: unknown) => {
					// Still update if menu is disabled after update request?
					// If already another menu is started to load during async load, cancel update
					if (state.loadingPath !== path) return;

					state.loadingPath = undefined; // Disable loading

					throw err; // rethrow
				});
		},
	});

	return bottomMenuState;
};

const bottomMenuState = createBottomMenuState();

export function useBottomMenu() {
	return {
		/** Get the data/methods from bottomMenuState */
		...useSnapshot<Readonly<Omit<IBottomMenuState, "createMenuAsync">>>(bottomMenuState), // Hide some members or make them readonly
	};
}
